
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import moment from "moment";
import { timeParser } from "@/filters";
import { getPIDeliveryData, updateContractualDeliveryDate } from "@/api/purchase-order";

@Component({
  name: "EditContractualDelivery",
})
export default class extends Vue {
  @Prop({ default: false }) private show!: boolean;
  @Prop({ default: null }) private selectedItem!: any;

  private loading = false;
  private submitting = false;
  private requestId = 0;
  private form = {
    date: "",
    piDeliveryDate: "",
  };

  private pickerOptions = {
    disabledDate: (time: Date) => this.isDateDisabled(time),
  };

  @Watch("show", { immediate: true })
  private onShowChange(visible: boolean) {
    if (visible) {
      this.load();
    }
  }

  private get invalid() {
    return !this.form.date || this.isDateDisabled(this.form.date);
  }

  private get remainingDays() {
    const deliveryDay = this.parseDay(this.form.date);
    if (!deliveryDay) {
      return null;
    }
    return deliveryDay.diff(moment().startOf("day"), "days");
  }

  private get remainingDaysNumber() {
    const value = this.remainingDays;
    if (value == null) {
      return "";
    }
    if (value < 0) {
      return "无";
    }
    return String(value);
  }

  private get remainingDaysClass() {
    const value = this.remainingDays;
    if (value == null) {
      return "";
    }
    if (value < 0) {
      return "is-danger";
    }
    if (value <= 10) {
      return "is-warn";
    }
    return "is-normal";
  }

  private get piDeliveryDateText() {
    const parsedDate = this.parseDay(this.form.piDeliveryDate);
    if (!parsedDate) {
      return "—";
    }
    return parsedDate.format("YYYY年MM月DD日");
  }

  private parseDay(date?: string) {
    if (!date) {
      return null;
    }
    const normalizedDate = String(date).split(" ")[0].split("T")[0];
    const strictDate = moment(normalizedDate, "YYYY-MM-DD", true);
    if (strictDate.isValid()) {
      return strictDate.startOf("day");
    }
    const parsedDate = moment(date);
    return parsedDate.isValid() ? parsedDate.startOf("day") : null;
  }

  private resolvePiDeliveryDate(res: any) {
    if (res == null || res === "") {
      return "";
    }
    if (typeof res === "string" || typeof res === "number") {
      return String(res);
    }
    const value = res.piDeliveryDate || res.deliveryDate || res.piDeliveryTime;
    return value == null ? "" : String(value);
  }

  private currentContractualDate(row?: any) {
    const text = timeParser(row && row.contractualDeliveryDate);
    return this.parseDay(text) ? text : "";
  }

  private isDateDisabled(date?: string | Date) {
    const current = date instanceof Date ? moment(date).startOf("day") : this.parseDay(date);
    if (!current || !current.isValid()) {
      return true;
    }
    if (current.isBefore(moment().startOf("day"))) {
      return true;
    }
    const piDeliveryDate = this.parseDay(this.form.piDeliveryDate);
    return !!(piDeliveryDate && current.isAfter(piDeliveryDate));
  }

  private async load() {
    const row = this.selectedItem;
    const currentRequestId = ++this.requestId;
    this.form = {
      date: this.currentContractualDate(row),
      piDeliveryDate: "",
    };
    if (!row || !row.id) {
      return;
    }
    this.loading = true;
    try {
      const res: any = await getPIDeliveryData({ id: row.id });
      if (currentRequestId !== this.requestId) {
        return;
      }
      this.form.piDeliveryDate = this.resolvePiDeliveryDate(res);
    } catch (err) {
      console.log(err);
    } finally {
      if (currentRequestId === this.requestId) {
        this.loading = false;
      }
    }
  }

  private handleClose() {
    this.requestId += 1;
    this.loading = false;
    this.submitting = false;
    this.$emit("update:show", false);
  }

  private async submit() {
    const row = this.selectedItem;
    const date = this.form.date;
    if (!row || !date) {
      this.$message.warning("请选择约定交期");
      return;
    }
    if (this.isDateDisabled(date)) {
      this.$message.warning("约定交期不能早于今天，也不能晚于PI交货日期");
      return;
    }
    const currentRequestId = this.requestId;
    this.submitting = true;
    try {
      await updateContractualDeliveryDate({ id: row.id, contractualDeliveryDate: date });
      this.$notify({
        title: "提示",
        message: "修改约定交期成功！",
        type: "success",
        duration: 2000,
      });
      if (currentRequestId === this.requestId) {
        this.$emit("update:show", false);
      }
      this.$emit("success");
    } catch (err) {
      console.log(err);
    } finally {
      if (currentRequestId === this.requestId) {
        this.submitting = false;
      }
    }
  }
}
